import React, { useEffect, useState } from "react";
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  onAuthStateChanged,
} from "firebase/auth";
import { auth } from "../firebase"; 

const LoginPage = ({ setCurrentPage }) => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    rememberMe: false,
  });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");

  // Zaten girişli kullanıcıyı otomatik yönlendir
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (user) {
        // kullanıcı girişliyse home'a geç
        setCurrentPage?.("home");
      }
    });
    return () => unsub();
  }, [setCurrentPage]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setErr("");
    setInfo("");
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setInfo("");
    setLoading(true);

    try {
      // Oturum kalıcılığı (beni hatırla)
      await setPersistence(
        auth,
        formData.rememberMe ? browserLocalPersistence : browserSessionPersistence
      );

      await signInWithEmailAndPassword(auth, formData.email.trim(), formData.password);
      setInfo("Başarıyla giriş yaptınız. Yönlendiriliyorsunuz…");
      setCurrentPage?.("home");
    } catch (error) {
      // Firebase hata kodlarını Türkçeleştir
      let msg = "Bir hata oluştu. Lütfen bilgilerinizi kontrol edin.";
      if (error.code === "auth/invalid-email") msg = "Geçersiz e-posta adresi.";
      if (error.code === "auth/user-disabled") msg = "Bu hesap devre dışı.";
      if (error.code === "auth/user-not-found") msg = "Bu e-posta ile kayıt bulunamadı.";
      if (error.code === "auth/wrong-password") msg = "Şifre hatalı.";
      if (error.code === "auth/too-many-requests")
        msg = "Çok fazla deneme yapıldı. Lütfen daha sonra tekrar deneyin.";
      setErr(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setErr("");
    setInfo("");
    const email = formData.email.trim();
    if (!email) {
      setErr("Şifre sıfırlamak için e-posta adresinizi girin.");
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      setInfo("Şifre sıfırlama bağlantısı e-posta adresinize gönderildi.");
    } catch (error) {
      let msg = "Şifre sıfırlama e-postası gönderilemedi.";
      if (error.code === "auth/invalid-email") msg = "Geçersiz e-posta adresi.";
      if (error.code === "auth/user-not-found") msg = "Bu e-posta ile kullanıcı bulunamadı.";
      setErr(msg);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-gray-100 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        {/* Logo ve Başlık */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-400 rounded-full mb-4">
            <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z" />
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-gray-800">Hoş Geldiniz</h2>
          <p className="text-gray-600 mt-2">Hesabınıza giriş yapın</p>
        </div>

        {/* Giriş Formu */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-6" noValidate>
            {/* Bilgilendirme / Hata */}
            {(err || info) && (
              <div
                className={`text-sm px-3 py-2 rounded ${
                  err ? "bg-red-50 text-red-700 border border-red-200" : "bg-green-50 text-green-700 border border-green-200"
                }`}
              >
                {err || info}
              </div>
            )}

            {/* E-posta */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">E-posta Adresi</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                  </svg>
                </div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
                  placeholder="ornek@email.com"
                  required
                  autoComplete="email"
                />
              </div>
            </div>

            {/* Şifre */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Şifre</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                />
              </div>
            </div>

            {/* Beni Hatırla ve Şifremi Unuttum */}
            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="rememberMe"
                  checked={formData.rememberMe}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-amber-400 focus:ring-amber-400 border-gray-300 rounded"
                />
                <span className="ml-2 block text-sm text-gray-700">Beni hatırla</span>
              </label>

              {/* anchor yerine button; a11y uyarısı çözümü */}
              <button
                type="button"
                onClick={handleForgotPassword}
                className="text-sm font-medium text-amber-600 hover:text-amber-500"
              >
                Şifremi unuttum?
              </button>
            </div>

            {/* Giriş Butonu */}
            <button
              type="submit"
              disabled={loading}
              className={`w-full bg-amber-400 hover:bg-amber-500 text-white font-semibold py-3 rounded-lg transition duration-200 flex items-center justify-center ${
                loading ? "opacity-70 cursor-not-allowed" : ""
              }`}
            >
              {loading ? (
                <svg className="animate-spin w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                </svg>
              ) : (
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                </svg>
              )}
              {loading ? "Giriş yapılıyor…" : "Giriş Yap"}
            </button>

            {/* Alt linkler */}
            <div className="text-center">
              <span className="text-sm text-gray-600">
                Hesabınız yok mu?{" "}
              </span>
              <button
                type="button"
                onClick={() => setCurrentPage?.("register")}
                className="text-sm font-medium text-amber-600 hover:text-amber-500"
              >
                Üye Ol
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
